import {
  addDisplayName,
  addHandlers,
  addProps,
  addState,
  addStateHandlers,
  flowMax,
} from 'ad-hok'
import { createUseStyles } from 'react-jss'
import { addApi } from 'shared/src/api'
import {
  addNavigate,
  addOptionalQueryParam,
} from 'shared/src/utils/addNavigate'
import { typedAs } from 'shared/src/utils/typedAs'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { ConstrainWidth } from 'utils/layout'
import { sansSerifFontFamily, serifFontFamily } from 'utils/styles'
import backButton from '../../assets/back-button-auth.svg'
import { TextField } from '../TextField'
import { EmailVerification } from './EmailVerification'
import { SetPassword } from './SetPassword'
import { TextButton } from './TextButton'
import { OmniAuth } from './OmniAuth'

const useStyles = createUseStyles({
  container: {
    paddingLeft: 24,
    paddingRight: 24,
    paddingTop: 24,
  },
  backButton: {
    background: 'none',
    border: 'none',
    padding: 0,
  },
  title: {
    color: '#ffffff',
    fontFamily: serifFontFamily,
    fontSize: 32,
    fontWeight: 700,
    lineHeight: '140%',
    letterSpacing: 0.01,
    marginTop: 16,
    marginBottom: 12,
    fontStyle: 'italic',
  },
  subtitle: {
    fontFamily: sansSerifFontFamily,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '187%',
    color: 'rgba(255, 255, 255, 0.56)',
    marginLeft: 30,
    marginBottom: 40,
  },
  italics: {
    fontStyle: 'italic',
    fontWeight: 600,
  },
  button: {
    height: 64,
    width: '100%',
    marginTop: 48,
    background:
      'radial-gradient(103.73% 266.85% at 0% 16.08%, #CF8355 0%, #CEAF85 38.44%, #9B876D 100%)',
    borderRadius: 2,
    color: '#ffffff',
    '&:disabled': {
      color: 'black',
    },
  },
  alreadyRegisteredText: {
    fontFamily: sansSerifFontFamily,
    fontWeight: 700,
    fontSize: 14,
    color: '#ffffff',
    lineHeight: '187%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 200,
    width: '100%',
    gap: 2,
  },
  linkText: {
    fontWeight: 900,
    color: '#CBA67A',
    '&:hover': {
      cursor: 'pointer',
    },
  },
})

type VerificationState = 'unknown' | 'pending' | 'verified' | 'invalid'

export const Register = flowMax(
  addDisplayName('Register'),
  addOptionalQueryParam('user'),
  addOptionalQueryParam('code'),
  addOptionalQueryParam('trigger'),
  addNavigate,
  addStateHandlers(
    ({ user }) => ({
      email: user ?? '',
      password: '',
    }),
    {
      onChangeEmail: () => (newEmail: string) => ({
        email: newEmail,
      }),
      onChangePassword: () => (newPassword: string) => ({
        password: newPassword,
      }),
    }
  ),
  addState('verificationState', 'setVerificationState', ({ user, code }) =>
    user && code
      ? typedAs<VerificationState>('verified')
      : typedAs<VerificationState>('unknown')
  ),
  addApi,
  addTranslationHelpers,
  addHandlers({
    onBack:
      ({ code, navigate }) =>
      () => {
        code ? navigate('/', { replace: true }) : navigate(-1)
      },
    onContinue:
      ({ api, setVerificationState, email }) =>
      () => {
        const getVerificationStatus = async () => {
          await api.auth.status(email, 'shopperWeb').then((response) => {
            if (response.status === 'notFound') {
              api.auth
                .createVerificationEmail({ email, source: 'creatorWeb' })
                .then(
                  () => {
                    setVerificationState('pending')
                  },
                  () => {
                    setVerificationState('invalid')
                  }
                )
            } else {
              setVerificationState('pending')
            }
          })
        }

        getVerificationStatus()
      },
    onClose:
      ({ setVerificationState }) =>
      () => {
        setVerificationState('unknown')
      },
    onLogin:
      ({ navigate }) =>
      () => {
        navigate('/login', { replace: true })
      },
  }),
  addHandlers({
    onKeyPressContinue:
      ({ onContinue, email }) =>
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && email !== '') {
          onContinue()
        }
      },
  }),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({
    email,
    onChangeEmail,
    trigger,
    onBack,
    onContinue,
    onClose,
    onLogin,
    onKeyPressContinue,
    verificationState,
    user,
    code,
    classes,
    t,
  }) => (
    <ConstrainWidth fillHeight>
      <div className={classes.container}>
        <button onClick={onBack} className={classes.backButton}>
          <img src={backButton} alt={t('back')} />
        </button>
        {verificationState === 'pending' ? (
          <>
            <EmailVerification
              emailType="verification"
              email={email}
              onClose={onClose}
            />
          </>
        ) : verificationState === 'verified' && user && code ? (
          <SetPassword email={user} code={code} />
        ) : (
          <>
            <h1 className={classes.title}>{t('auth.register.title')}</h1>
            <p className={classes.subtitle}>
              {t(
                trigger === 'login'
                  ? 'auth.register.attemptedLoginSubtitle'
                  : 'auth.register.subtitle'
              )}
            </p>
            <TextField
              id="email"
              label={t('auth.fields.email')}
              value={email ?? ''}
              onChange={onChangeEmail}
              onKeyPress={onKeyPressContinue}
            />
            {verificationState === 'invalid' && (
              <p className={classes.subtitle}>{t('auth.invalidEmail')}</p>
            )}
            <TextButton
              text={t('auth.continue')}
              onClick={onContinue}
              disabled={email === ''}
            />
            <OmniAuth />
            <div className={classes.alreadyRegisteredText}>
              {t('auth.register.alreadyRegistered')}
              <span className={classes.linkText} onClick={onLogin}>
                {t('auth.register.login')}
              </span>
            </div>
          </>
        )}
      </div>
    </ConstrainWidth>
  )
)
